import React, { useEffect, useState, useCallback, useRef } from 'react';
import type { TutorialStep } from '../../types';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '../../icons';

interface StepTooltipProps {
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  targetSelector: string;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  zIndex?: number;
}

type Position = {
  top: number;
  left: number;
  arrowSide: 'top' | 'bottom' | 'left' | 'right';
};

const TOOLTIP_OFFSET = 12;
const ARROW_SIZE = 8;
const TOOLTIP_MAX_WIDTH = 320;

export function StepTooltip({
  step,
  stepIndex,
  totalSteps,
  targetSelector,
  onNext,
  onPrev,
  onSkip,
  zIndex = 61,
}: StepTooltipProps) {
  const [pos, setPos] = useState<Position>({ top: 0, left: 0, arrowSide: 'top' });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  const calculate = useCallback(() => {
    const target = document.querySelector(targetSelector);
    const tooltip = tooltipRef.current;
    if (!target || !tooltip) return;

    const tr = target.getBoundingClientRect();
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const preferred = step.placement ?? 'bottom';
    const padding = 8;

    // Calculate position for each placement, then pick the one that fits
    const placements: Record<string, Position> = {
      bottom: {
        top: tr.bottom + TOOLTIP_OFFSET,
        left: Math.max(padding, Math.min(tr.left + tr.width / 2 - tw / 2, vw - tw - padding)),
        arrowSide: 'top',
      },
      top: {
        top: tr.top - th - TOOLTIP_OFFSET,
        left: Math.max(padding, Math.min(tr.left + tr.width / 2 - tw / 2, vw - tw - padding)),
        arrowSide: 'bottom',
      },
      right: {
        top: Math.max(padding, Math.min(tr.top + tr.height / 2 - th / 2, vh - th - padding)),
        left: tr.right + TOOLTIP_OFFSET,
        arrowSide: 'left',
      },
      left: {
        top: Math.max(padding, Math.min(tr.top + tr.height / 2 - th / 2, vh - th - padding)),
        left: tr.left - tw - TOOLTIP_OFFSET,
        arrowSide: 'right',
      },
    };

    const fits = (p: Position) =>
      p.top >= padding &&
      p.left >= padding &&
      p.top + th <= vh - padding &&
      p.left + tw <= vw - padding;

    // Prefer the requested placement, fall through to alternatives
    const order = [preferred, 'bottom', 'top', 'right', 'left'] as const;
    let chosen = placements[preferred];
    for (const dir of order) {
      if (fits(placements[dir])) {
        chosen = placements[dir];
        break;
      }
    }

    setPos(chosen);
  }, [targetSelector, step.placement]);

  useEffect(() => {
    // Initial positioning after first render so we know tooltip dimensions
    requestAnimationFrame(calculate);
  }, [calculate]);

  useEffect(() => {
    const onUpdate = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(calculate);
    };
    window.addEventListener('scroll', onUpdate, true);
    window.addEventListener('resize', onUpdate);
    return () => {
      window.removeEventListener('scroll', onUpdate, true);
      window.removeEventListener('resize', onUpdate);
      cancelAnimationFrame(rafRef.current);
    };
  }, [calculate]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onNext, onPrev, onSkip]);

  const arrowStyles = buildArrowStyles(pos.arrowSide);

  return (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex,
        maxWidth: TOOLTIP_MAX_WIDTH,
        width: 'max-content',
        background: 'var(--sw-bg-surface, #22223b)',
        border: '1px solid var(--sw-border, #2d2d44)',
        borderRadius: 'var(--sw-border-radius, 8px)',
        padding: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        color: 'var(--sw-text-primary, #e0e0e0)',
        fontFamily: 'var(--sw-font-family)',
        transition: 'top 0.2s ease, left 0.2s ease',
      }}
    >
      {/* Arrow */}
      <div style={arrowStyles} />

      {/* Header: step counter + skip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <span
          style={{
            fontSize: '11px',
            fontWeight: 500,
            color: 'var(--sw-accent-primary, #4cc9f0)',
          }}
        >
          {stepIndex + 1} of {totalSteps}
        </span>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Skip tutorial"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--sw-text-secondary, #9a9ab0)',
            padding: '4px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseIcon size={14} />
        </button>
      </div>

      {/* Title */}
      <h4
        style={{
          margin: '0 0 6px',
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--sw-text-primary, #e0e0e0)',
        }}
      >
        {step.title}
      </h4>

      {/* Content */}
      <p
        style={{
          margin: '0 0 14px',
          fontSize: '13px',
          lineHeight: 1.55,
          color: 'var(--sw-text-secondary, #9a9ab0)',
        }}
      >
        {step.content}
      </p>

      {/* Navigation buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onPrev}
            aria-label="Previous step"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              minWidth: '48px',
              minHeight: '48px',
              borderRadius: 'var(--sw-border-radius, 8px)',
              border: '1px solid var(--sw-border, #2d2d44)',
              background: 'transparent',
              color: 'var(--sw-text-secondary, #9a9ab0)',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            <ChevronLeftIcon size={14} />
            Back
          </button>
        )}

        <button
          type="button"
          onClick={onNext}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '6px 14px',
            minWidth: '48px',
            minHeight: '48px',
            borderRadius: 'var(--sw-border-radius, 8px)',
            border: 'none',
            background: 'var(--sw-accent-primary, #4cc9f0)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          {isLast ? 'Done' : 'Next'}
          {!isLast && <ChevronRightIcon size={14} />}
        </button>
      </div>
    </div>
  );
}

function buildArrowStyles(
  side: 'top' | 'bottom' | 'left' | 'right',
): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    border: `${ARROW_SIZE}px solid transparent`,
  };

  switch (side) {
    case 'top':
      return {
        ...base,
        top: -ARROW_SIZE * 2,
        left: '50%',
        marginLeft: -ARROW_SIZE,
        borderBottomColor: 'var(--sw-bg-surface, #22223b)',
      };
    case 'bottom':
      return {
        ...base,
        bottom: -ARROW_SIZE * 2,
        left: '50%',
        marginLeft: -ARROW_SIZE,
        borderTopColor: 'var(--sw-bg-surface, #22223b)',
      };
    case 'left':
      return {
        ...base,
        left: -ARROW_SIZE * 2,
        top: '50%',
        marginTop: -ARROW_SIZE,
        borderRightColor: 'var(--sw-bg-surface, #22223b)',
      };
    case 'right':
      return {
        ...base,
        right: -ARROW_SIZE * 2,
        top: '50%',
        marginTop: -ARROW_SIZE,
        borderLeftColor: 'var(--sw-bg-surface, #22223b)',
      };
  }
}
